import { createRouter, createWebHistory } from 'vue-router'

import Login from '../components/Login.vue'
import UserDashboard from '../components/UserDashboard.vue'
import ManagerDashboard from '../components/ManagerDashboard.vue'
import WorkerShifts from '../components/WorkerShifts.vue'
import UserInfo from '../components/UserInfo.vue'
import TimeReporting from '../components/TimeReporting.vue'
import UserManagement from '../components/UserManagement.vue'
import UserProfile from '../components/UserProfile.vue'
import ShiftCalendar from '../components/ShiftCalendar.vue'
import SuperAdminDashboard from '../components/SuperAdminDashboard.vue'
import CompanySettings from '../components/CompanySettings.vue'

// Admin components
import AdminLogin from '../components/admin/AdminLogin.vue'
import AdminDashboard from '../components/admin/AdminDashboard.vue'
import CompanyManager from '../components/admin/CompanyManager.vue'
import InvoiceManager from '../components/admin/InvoiceManager.vue'

// AI Scheduler components
import AISchedulerDashboard from '../components/ai-scheduler/AISchedulerDashboard.vue'

import NotFound from '../components/NotFound.vue'

const routes = [
  { path: '/', name: 'Login', component: Login },
  { path: '/dashboard', name: 'UserDashboard', component: UserDashboard },
  { path: '/manager', name: 'ManagerDashboard', component: ManagerDashboard, meta: { requiredRole: 'manager' } },
  { path: '/shifts', name: 'ShiftCalendar', component: ShiftCalendar, meta: { requiredRole: 'manager' } },
  { path: '/worker/:workerId', name: 'WorkerShifts', component: WorkerShifts },
  { path: '/user-info', name: 'UserInfo', component: UserInfo },
  { path: '/profile', name: 'UserProfile', component: UserProfile },
  { path: '/time-reporting', name: 'TimeReporting', component: TimeReporting },
  { path: '/user-management', name: 'UserManagement', component: UserManagement, meta: { requiredRole: 'manager' } },
  { path: '/company-settings', name: 'CompanySettings', component: CompanySettings, meta: { requiredRole: 'manager' } },
  { path: '/superadmin', name: 'SuperAdminDashboard', component: SuperAdminDashboard, meta: { requiredRole: 'superadmin' } },
  
  // SaaS Management Routes (completely separate from /admin which is Django admin)
  { path: '/saas/login', name: 'AdminLogin', component: AdminLogin },
  { path: '/saas', redirect: '/saas/dashboard' }, // Smart redirect handled by route guard
  { path: '/saas/dashboard', name: 'AdminDashboard', component: AdminDashboard, meta: { requiresAdminAuth: true } },
  { path: '/saas/companies', name: 'CompanyManager', component: CompanyManager, meta: { requiresAdminAuth: true } },
  { path: '/saas/invoices', name: 'InvoiceManager', component: InvoiceManager, meta: { requiresAdminAuth: true } },
  
  // AI Scheduler routes
  { 
    path: '/saas/scheduling', 
    name: 'AIScheduler', 
    component: AISchedulerDashboard, 
    meta: { requiresAdminAuth: true } 
  },
  
  // Catch-all 404 route (must be last)
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFound },
]


import { useAuthStore } from '../stores/auth.js'
import { useAdminStore } from '../stores/admin.js'

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Navigation guard for role-based access and admin authentication
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const adminStore = useAdminStore()
  
  // Handle SaaS management routes
  if (to.path.startsWith('/saas')) {
    // Allow login page without session check to avoid redirect loop
    if (to.path === '/saas/login') {
      return next()
    }
    
    // For protected routes, check authentication
    if (to.meta.requiresAdminAuth) {
      // Check admin authentication (PublicAdminJWT)
      const isAdminAuthenticated = await adminStore.checkSession()
      
      // Check tenant authentication - but ONLY if we don't have admin auth
      // Admin users (admin_denis) should not be treated as tenant users
      const hasTenantAuth = !isAdminAuthenticated && authStore.isAuthenticated
      const isTenantSuperuser = hasTenantAuth && authStore.user?.is_superuser
      
      // Allow access if authenticated with either system
      if (!isAdminAuthenticated && !isTenantSuperuser) {
        // If coming from tenant system, redirect to admin login
        if (hasTenantAuth) {
          return next('/saas/login')
        } else {
          return next('/saas/login')
        }
      }
      
      // Route-specific access control
      if (to.path === '/saas/companies' || to.path === '/saas/invoices') {
        // Company/invoice management requires admin authentication (public schema)
        if (!isAdminAuthenticated) {
          if (isTenantSuperuser) {
            return next('/saas/scheduling')
          } else {
            return next('/saas/dashboard')
          }
        }
      }
      
      // AI Scheduler access control
      if (to.path === '/saas/scheduling') {
        // AI scheduler is accessible to both admin and tenant superusers
        if (!isAdminAuthenticated && !isTenantSuperuser) {
          return next('/saas/login')
        }
      }
      
      // Auto-redirect /saas root to appropriate dashboard
      if (to.path === '/saas') {
        if (isAdminAuthenticated) {
          // Admin user - go to company management dashboard
          return next('/saas/dashboard')
        } else if (isTenantSuperuser) {
          // Tenant superuser - redirect to AI scheduler
          return next('/saas/scheduling')
        } else {
          // Fallback to main dashboard
          return next('/saas/dashboard')
        }
      }
    }
    
    return next()
  }
  
  // Handle regular user routes with role requirements
  const userRole = authStore.getUserRole
  const requiredRole = to.meta.requiredRole

  if (requiredRole) {
    // Allow superadmin to access all manager pages
    if (requiredRole === 'manager' && (userRole === 'manager' || userRole === 'superadmin')) {
      return next()
    }
    if (userRole !== requiredRole) {
      // Show 404 page for forbidden access
      return next({ name: 'NotFound', params: { pathMatch: to.path.substring(1).split('/') }, query: to.query, hash: to.hash })
    }
  }
  next()
})

export default router
